import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {noop} from '@github-ui/noop'
import {Link, useLocation, useParams} from '@github-ui/react-router'
import {SidebarList} from '@github-ui/shared-dashboard-sidebar/SidebarList'
import {isStaff} from '@github-ui/stats'
import {MilestoneIcon, StackIcon, TableIcon, TagIcon} from '@primer/octicons-react'
import {NavList} from '@primer/react'
import {useMemo} from 'react'

import {repoSidebarLinks} from '../../constants/repo-sidebar-links'
import {useIssuesSidebarState} from '../../hooks/use-issues-sidebar-state'
import {getRepoIssuesQueryPath} from '../../utils/urls'
import styles from './RepoIssuesSidebar.module.css'

const PUBLIC_FEEDBACK_URL = 'https://gh.io/issues-sidebar-feedback'
const STAFF_FEEDBACK_URL = 'https://gh.io/issues-sidebar-feedback-staff'

export function RepoIssuesSidebar() {
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const {pathname} = useLocation()
  const {selectedLink} = useIssuesSidebarState({owner: owner ?? '', repo: repo ?? ''})
  const repoPath = `/${owner}/${repo}`
  const labelsPath = `${repoPath}/labels`
  const issuesPath = `${repoPath}/issues`
  const projectsPath = `${repoPath}/projects`
  const milestonesPath = `${repoPath}/milestones`
  const milestonePath = `${repoPath}/milestone/`
  const viewsPath = `${issuesPath}/views`

  const linksWithPaths = useMemo(
    () =>
      repoSidebarLinks.map(link => ({
        ...link,
        path: getRepoIssuesQueryPath({owner: owner ?? '', repo: repo ?? '', query: link.query}),
      })),
    [owner, repo],
  )

  return (
    <aside>
      <SidebarList
        dashboardType="issues"
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
      />

      <hr className={styles.divider} />

      <NavList aria-label="Pages">
        <NavList.Item aria-current={pathname === projectsPath && 'page'} href={projectsPath}>
          <NavList.LeadingVisual>
            <TableIcon />
          </NavList.LeadingVisual>
          Projects
        </NavList.Item>
        {isFeatureEnabled('issue_pinned_views_index_page') && (
          <NavList.Item aria-current={pathname === viewsPath && 'page'} as={Link} to={viewsPath}>
            <NavList.LeadingVisual>
              <StackIcon />
            </NavList.LeadingVisual>
            Views
          </NavList.Item>
        )}
        <NavList.Item
          aria-current={(pathname === milestonesPath || pathname.startsWith(milestonePath)) && 'page'}
          as={Link}
          to={milestonesPath}
        >
          <NavList.LeadingVisual>
            <MilestoneIcon />
          </NavList.LeadingVisual>
          Milestones
        </NavList.Item>
        <NavList.Item
          aria-current={(pathname === labelsPath || pathname === `${repoPath}/issues/labels`) && 'page'}
          as={Link}
          to={labelsPath}
        >
          <NavList.LeadingVisual>
            <TagIcon />
          </NavList.LeadingVisual>
          Labels
        </NavList.Item>
      </NavList>

      <BetaLabel className={styles.betaLabel} feedbackUrl={isStaff() ? STAFF_FEEDBACK_URL : PUBLIC_FEEDBACK_URL} />
    </aside>
  )
}
